import type { CompiledProjectTrack } from '../app/ride-track.ts';
import type { QualityTier } from '../scene/quality.ts';
import type { SemanticSectionType } from '../track/index.ts';

export type ProductionHardwareRole =
  | 'rail-connector'
  | 'support-saddle'
  | 'support-cap'
  | 'support-footing'
  | 'support-brace-connector'
  | 'support-brace'
  | 'lift-chain-link'
  | 'lift-anti-rollback'
  | 'lift-trough'
  | 'lift-tensioner'
  | 'lift-crest-drive'
  | 'lift-service-catwalk'
  | 'launch-stator'
  | 'launch-conduit'
  | 'brake-fin'
  | 'brake-actuator'
  | 'photo-camera'
  | 'photo-lens'
  | 'photo-flash'
  | 'photo-base-rail'
  | 'photo-crossbeam'
  | 'photo-post';

export interface ProductionHardwarePlacement {
  readonly role: ProductionHardwareRole;
  readonly distance: number;
  readonly rightOffset: number;
  readonly upOffset: number;
  readonly forwardOffset?: number;
  readonly localYawRadians?: number;
  readonly grounded?: boolean;
  readonly motionSpeedMps?: number;
  readonly motionStartDistance?: number;
  readonly motionEndDistance?: number;
  readonly priority: 0 | 1 | 2;
}

export interface ProductionSectionRun {
  readonly type: SemanticSectionType;
  readonly startDistance: number;
  readonly endDistance: number;
  readonly motionSpeedMps: number | null;
}

export const PRODUCTION_LIFT_CHAIN_PITCH_METERS = 0.44;
export const DEFAULT_PRODUCTION_LIFT_SPEED_MPS = 4.5;

export function advanceProductionLiftChainPhase(
  currentPhase: number,
  deltaSeconds: number,
  speedMps: number,
  timeScale = 1,
): number {
  const phase = Number.isFinite(currentPhase) ? Math.max(0, currentPhase) : 0;
  const elapsed = Number.isFinite(deltaSeconds) ? Math.max(0, deltaSeconds) : 0;
  const speed = Number.isFinite(speedMps) ? Math.max(0, speedMps) : 0;
  const scale = Number.isFinite(timeScale) ? Math.max(0, timeScale) : 0;
  return (phase + elapsed * speed * scale) % PRODUCTION_LIFT_CHAIN_PITCH_METERS;
}

const spacingForQuality = (quality: QualityTier) =>
  quality === 'high'
    ? {
        chain: PRODUCTION_LIFT_CHAIN_PITCH_METERS,
        rack: 0.72,
        trough: 1.1,
        powered: 1.35,
        catwalk: 1.2,
      }
    : quality === 'medium'
      ? {
          chain: PRODUCTION_LIFT_CHAIN_PITCH_METERS,
          rack: 1.05,
          trough: 1.55,
          powered: 1.8,
          catwalk: 1.8,
        }
      : {
          chain: PRODUCTION_LIFT_CHAIN_PITCH_METERS,
          rack: 1.5,
          trough: 2.2,
          powered: 2.6,
          catwalk: 2.4,
        };

function spaced(start: number, end: number, spacing: number): readonly number[] {
  const length = Math.max(0, end - start);
  const count = Math.max(1, Math.floor(length / spacing));
  return Array.from({ length: count }, (_, index) => start + ((index + 0.5) / count) * length);
}

function touchingSpaced(start: number, end: number, maximumSpacing: number): readonly number[] {
  const length = Math.max(0, end - start);
  const count = Math.max(1, Math.ceil(length / maximumSpacing));
  return Array.from({ length: count }, (_, index) => start + ((index + 0.5) / count) * length);
}

export function productionSectionRuns(
  compiled: CompiledProjectTrack,
): readonly ProductionSectionRun[] {
  const runs: ProductionSectionRun[] = [];
  for (const span of compiled.spans) {
    const midpoint = (span.startDistance + span.endDistance) * 0.5;
    const section = compiled.sampler.sampleByDistance(midpoint).section;
    const type = section?.type ?? 'normal';
    const requestedSpeed = section?.parameters?.targetSpeed;
    const motionSpeedMps =
      type === 'lift'
        ? typeof requestedSpeed === 'number' && Number.isFinite(requestedSpeed)
          ? Math.max(0, requestedSpeed)
          : DEFAULT_PRODUCTION_LIFT_SPEED_MPS
        : null;
    const previous = runs.at(-1);
    if (
      previous?.type === type &&
      previous.motionSpeedMps === motionSpeedMps &&
      Math.abs(previous.endDistance - span.startDistance) < 1e-4
    ) {
      runs[runs.length - 1] = { ...previous, endDistance: span.endDistance };
    } else
      runs.push({
        type,
        startDistance: span.startDistance,
        endDistance: span.endDistance,
        motionSpeedMps,
      });
  }
  return runs;
}

export function buildProductionHardwarePlan(
  compiled: CompiledProjectTrack,
  supportDistances: readonly number[],
  quality: QualityTier,
  maximumInstances: number,
): readonly ProductionHardwarePlacement[] {
  const result: ProductionHardwarePlacement[] = [];
  const add = (
    role: ProductionHardwareRole,
    distance: number,
    rightOffset: number,
    upOffset: number,
    priority: 0 | 1 | 2,
    grounded = false,
    forwardOffset = 0,
    localYawRadians = 0,
    motionSpeedMps?: number,
    motionStartDistance?: number,
    motionEndDistance?: number,
  ) =>
    result.push({
      role,
      distance,
      rightOffset,
      upOffset,
      priority,
      grounded,
      forwardOffset,
      localYawRadians,
      motionSpeedMps,
      motionStartDistance,
      motionEndDistance,
    });
  const spacing = spacingForQuality(quality);

  for (const run of productionSectionRuns(compiled)) {
    if (run.type === 'lift') {
      for (const distance of touchingSpaced(run.startDistance, run.endDistance, spacing.chain))
        add(
          'lift-chain-link',
          distance,
          0,
          0.17,
          1,
          false,
          0,
          0,
          run.motionSpeedMps ?? DEFAULT_PRODUCTION_LIFT_SPEED_MPS,
          run.startDistance,
          run.endDistance,
        );
      for (const distance of spaced(run.startDistance, run.endDistance, spacing.rack))
        add('lift-anti-rollback', distance, 0.29, 0.16, 1);
      for (const distance of spaced(run.startDistance, run.endDistance, spacing.trough))
        add('lift-trough', distance, 0, 0.045, 1);
      for (const distance of spaced(run.startDistance, run.endDistance, spacing.catwalk))
        add('lift-service-catwalk', distance, 1.04, -0.12, 2);
      add(
        'lift-tensioner',
        run.startDistance + Math.min(0.35, (run.endDistance - run.startDistance) * 0.2),
        -0.82,
        -0.08,
        0,
      );
      add(
        'lift-crest-drive',
        run.endDistance - Math.min(0.42, (run.endDistance - run.startDistance) * 0.2),
        0.86,
        -0.05,
        0,
      );
    } else if (run.type === 'launch') {
      for (const distance of spaced(run.startDistance, run.endDistance, spacing.powered)) {
        add('launch-stator', distance, -0.31, 0.12, 1);
        add('launch-stator', distance, 0.31, 0.12, 1);
        add('launch-conduit', distance, 0.59, -0.08, 2);
      }
    } else if (run.type === 'brake') {
      for (const distance of spaced(run.startDistance, run.endDistance, spacing.powered)) {
        add('brake-fin', distance, 0, 0.18, 1);
        add('brake-actuator', distance, -0.32, 0.08, 2);
        add('brake-actuator', distance, 0.32, 0.08, 2);
      }
    } else if (run.type === 'photo') {
      const distance = (run.startDistance + run.endDistance) * 0.5;
      add('photo-post', distance, -1.18, 1.37, 0);
      add('photo-post', distance, 1.18, 1.37, 0);
      add('photo-base-rail', distance, -0.93, 0.08, 0);
      add('photo-base-rail', distance, 0.93, 0.08, 0);
      add('photo-crossbeam', distance, 0, 2.68, 0);
      add('photo-camera', distance, 0, 2.43, 0, false, -0.03);
      add('photo-lens', distance, 0, 2.43, 0, false, -0.28);
      add('photo-flash', distance, -0.68, 2.48, 0, false, -0.16);
      add('photo-flash', distance, 0.68, 2.48, 0, false, -0.16);
    }
  }

  for (const distance of [...new Set(supportDistances.map((value) => Number(value.toFixed(4))))]) {
    add('support-saddle', distance, 0, -0.15, 0);
    add('support-footing', distance, 0, 0.08, 0, true);
    add('support-brace-connector', distance, -0.52, -0.32, 1);
    add('support-brace-connector', distance, 0.52, -0.32, 1);
    add('support-brace', distance, -0.92, 0, 2, true, -0.24);
    add('support-brace', distance, 0.92, 0, 2, true, 0.24);
  }
  for (const span of compiled.spans) add('rail-connector', span.startDistance, 0, 0, 2);

  if (result.length <= maximumInstances) return result;
  return result
    .map((placement, index) => ({ placement, index }))
    .sort(
      (left, right) =>
        left.placement.priority - right.placement.priority || left.index - right.index,
    )
    .slice(0, maximumInstances)
    .sort((left, right) => left.index - right.index)
    .map(({ placement }) => placement);
}
